import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, ShoppingBasket, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { Loader, PageHeader } from "@/components/common";
import { useLiveRefresh } from "@/lib/live";
import { AMBER, MEALS, MEAL_LABEL, addDays, dayNum, dayShort } from "@/lib/meals";
import { Warnings } from "@/components/meals/parts";
import SlotDialog from "@/components/meals/SlotDialog";
import PlanWeekDialog from "@/components/meals/PlanWeekDialog";
import IngredientsDialog from "@/components/meals/IngredientsDialog";
import RecipeBox from "@/components/meals/RecipeBox";

function Slot({ week, date, meal, onOpen, onDrop }) {
  const s = week.slots.find((x) => x.date === date && x.meal === meal);
  const edit = week.can_edit;
  return (
    <div
      onDragOver={(e) => edit && e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); const src = e.dataTransfer.getData("text/plain"); if (src) onDrop(src, date, meal); }}
      className="min-w-0 space-y-1"
    >
      <span className="block text-[10px] font-black uppercase tracking-[0.12em] text-q-faint">{MEAL_LABEL[meal]}</span>
      <button
        draggable={!!(edit && s)}
        onDragStart={(e) => e.dataTransfer.setData("text/plain", `${date}|${meal}`)}
        disabled={!edit && !s}
        onClick={() => (edit ? onOpen({ date, meal }) : null)}
        className={`min-h-[44px] w-full min-w-0 rounded-xl border px-3 py-2 text-left text-sm transition-colors ${s ? "border-q-line bg-q-raised" : "border-dashed border-q-line text-q-faint"} ${edit ? "hover:border-[rgb(var(--glow-lists))]" : "cursor-default"}`}
        data-testid={`slot-${date}-${meal}`}
      >
        {s ? (
          <>
            <span className="block break-words font-semibold text-q-text" data-testid={`slot-name-${date}-${meal}`}>{s.emoji ? `${s.emoji} ` : ""}{s.name}{s.cook_name ? ` · ${s.cook_name} cooks` : ""}</span>
            {s.note && <span className="block truncate text-[11px] text-q-muted" data-testid={`slot-note-${date}-${meal}`}>{s.note}</span>}
            <Warnings items={s.warnings} compact testid={`slot-warning-${date}-${meal}`} />
          </>
        ) : edit ? <span className="inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add</span> : "—"}
      </button>
    </div>
  );
}

function WeekView({ hub, week, reload }) {
  const [slot, setSlot] = useState(null);
  const [recipes, setRecipes] = useState([]);
  useEffect(() => { if (week.can_edit) api.get(`/organizations/${hub.id}/meals/recipes`).then(({ data }) => setRecipes(data.recipes)).catch(() => {}); }, [hub.id, week.can_edit, slot]);
  const drop = async (src, date, meal) => {
    const [fd, fm] = src.split("|");
    if (fd === date && fm === meal) return;
    try { await api.post(`/organizations/${hub.id}/meals/slots/move`, { from_date: fd, from_meal: fm, to_date: date, to_meal: meal }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="meals-week">
      {week.days.map((d) => (
        <section key={d} className={`q-card min-w-0 space-y-2 ${d === week.today ? "ring-1 ring-[rgb(var(--glow-lists))]" : ""}`} data-testid={`day-${d}`}>
          <h3 className="flex items-baseline gap-2 font-display font-extrabold text-q-text">{dayShort(d)} <span className="text-xs font-semibold text-q-faint">{dayNum(d)}</span>{d === week.today && <span className="ml-auto text-[10px] font-black uppercase tracking-[0.14em] text-[rgb(var(--glow-lists))]">Today</span>}</h3>
          {MEALS.map((m) => <Slot key={m} week={week} date={d} meal={m} onOpen={setSlot} onDrop={drop} />)}
        </section>
      ))}
      {slot && <SlotDialog hubId={hub.id} week={week} slot={slot} recipes={recipes} onClose={() => setSlot(null)} onSaved={reload} />}
    </div>
  );
}

export default function Meals() {
  const { hub } = useHub();
  const [tab, setTab] = useState("week");
  const [start, setStart] = useState(null);
  const [week, setWeek] = useState(null);
  const [box, setBox] = useState(null);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [dialog, setDialog] = useState("");
  const base = `/organizations/${hub.id}/meals`;
  const loadWeek = useCallback(() => api.get(`${base}/week`, { params: { start: start || undefined } }).then(({ data }) => setWeek(data)).catch((e) => toast.error(errMsg(e))), [base, start]);
  const loadBox = useCallback(() => api.get(`${base}/recipes`, { params: { q: q || undefined, tag: tag || undefined } }).then(({ data }) => setBox(data)).catch(() => {}), [base, q, tag]);
  useEffect(() => { loadWeek(); }, [loadWeek]);
  useEffect(() => { const id = setTimeout(loadBox, 200); return () => clearTimeout(id); }, [loadBox]);
  useLiveRefresh(() => { loadWeek(); loadBox(); }, (m) => /^(meal\.|recipe\.|emergency\.card)/.test(m.type || ""));
  if (!week) return <Loader />;
  const tabBtn = (id, label) => <button className={`min-h-[44px] rounded-full px-5 text-sm font-bold ${tab === id ? "bg-[rgba(var(--glow-lists),0.18)] text-q-text" : "text-q-muted"}`} onClick={() => setTab(id)} data-testid={`meals-tab-${id}`}>{label}</button>;
  return (
    <div data-testid="meals-page">
      <PageHeader eyebrow="Lists" title="Meals" rgb={AMBER} testid="meals-title" />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex rounded-full border border-q-line p-1">{tabBtn("week", "Week")}{tabBtn("recipes", "Recipes")}</div>
        {tab === "week" && week.can_edit && <button className="q-btn q-btn-primary" onClick={() => setDialog("plan")} data-testid="plan-week-btn"><Sparkles className="h-4 w-4" /> Plan my week</button>}
        {tab === "week" && week.can_add_to_list && <button className="q-btn q-btn-secondary" onClick={() => setDialog("ings")} data-testid="add-ingredients-btn"><ShoppingBasket className="h-4 w-4" /> Add this week's ingredients</button>}
      </div>
      {tab === "week" ? (
        <>
          <div className="mb-4 flex items-center gap-2" data-testid="week-nav">
            <button className="q-btn q-btn-ghost w-11 px-0" aria-label="Previous week" onClick={() => setStart(addDays(week.start, -7))} data-testid="week-prev-btn"><ChevronLeft className="h-5 w-5" /></button>
            <button className="q-btn q-btn-secondary" onClick={() => setStart(null)} data-testid="week-this-btn">This week</button>
            <button className="q-btn q-btn-ghost w-11 px-0" aria-label="Next week" onClick={() => setStart(addDays(week.start, 7))} data-testid="week-next-btn"><ChevronRight className="h-5 w-5" /></button>
            <span className="text-sm font-semibold text-q-muted" data-testid="week-range">{dayNum(week.days[0])} – {dayNum(week.days[6])}</span>
          </div>
          <WeekView hub={hub} week={week} reload={loadWeek} />
        </>
      ) : box ? <RecipeBox hubId={hub.id} data={box} q={q} setQ={setQ} tag={tag} setTag={setTag} reload={() => { loadBox(); loadWeek(); }} /> : <Loader />}
      {dialog === "plan" && <PlanWeekDialog hubId={hub.id} week={week} onClose={() => setDialog("")} onSaved={() => { loadWeek(); loadBox(); }} />}
      {dialog === "ings" && <IngredientsDialog hubId={hub.id} start={week.start} onClose={() => setDialog("")} />}
    </div>
  );
}
